import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { FaFileExcel, FaCalculator } from 'react-icons/fa';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';
import { dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import { StarIcon } from '@/shared/components/Icons';
import Checkbox from '@/shared/components/input/Checkbox';
import Button from '@/shared/components/Button';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import CurrencySymbol from '@/shared/components/CurrencySymbol';
import { CanUpdate } from '@/shared/components/security';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import SignalRService from '@/shared/services/SignalRService';
import messages from './priceGridMessages';
import RecalcRegistrationPriceModal from './RecalcRegistrationPriceModal';
import PriceGridAmountModal from './PriceGridAmountModal';
import PriceGridCell from './PriceGridCell';

const resourceTypesProp = ['time', 'cost', 'travel'];

class PriceGrid extends Component {
  state = {
    grid: null,
    profiles: null,
    time: null,
    cost: null,
    travel: null,
    showUpdateAmountModal: false,
    showRecalcRegistrationPriceModal: false,
  };

  componentDidMount() {
    const { gridId } = this.props;
    this.reloadGrid(gridId);
    SignalRService.subscribe('jobUpdated', this.jobUpdateHandler);
  }
  componentWillUnmount() {
    SignalRService.unsubscribe('jobUpdated', this.jobUpdateHandler);
  }
  reloadGrid = gridId => {
    if (!gridId) return;
    const { intl } = this.props;
    api.base
      .get(`${config.appUrls.api}/api/Price/Grid/${gridId}`)
      .then(({ data }) => this.MapPriceGridScreen(data, intl));
  };
  jobUpdateHandler = job => {
    const { gridId } = this.props;
    if (
      job.jobType === 56 &&
      (job.status === 2 || job.status === 3) &&
      job.parameters &&
      typeof job.parameters === 'string'
    ) {
      const params = JSON.parse(job.parameters);
      if (!params) return;
      if (params.updateAllPriceGrids || params.priceGridId === gridId) {
        this.reloadGrid(gridId);
      }
    }
  };
  componentDidUpdate({ gridId: oldGridId }) {
    const { gridId } = this.props;
    if (gridId !== oldGridId) {
      this.reloadGrid(gridId);
    }
  }

  updateCell = (type, rowId, colId, cell) => {
    const resourceProp = resourceTypesProp[type];
    api.base.put(`${config.appUrls.api}/api/Price/Item/`, cell).then(({ data }) => {
      this.setState(state => ({
        ...state,
        [resourceProp]: state[resourceProp].map(row => {
          if (row.id === rowId) {
            return { ...row, cells: { ...row.cells, [colId]: data } };
          }
          return row;
        }),
      }));
      return data;
    });
  };

  mapProfileHeader = profile => {
    const { intl } = this.props;

    return (
      <th key={profile.id} className="profile">
        <FormattedListValue list="employeeprofile" id={profile.id} fallback={profile.name} />
        {profile.isDefault && (
          <StarIcon className="margin5L text-ochre" title={intl.formatMessage(messages.Default)} />
        )}
      </th>
    );
  };

  handlePriceGridSelect = (selectedRow, type) => {
    this.setState(state => ({
      ...state,
      time:
        type === 0
          ? state.time.map(row => {
              if (row.id === selectedRow.id) {
                return {
                  ...row,
                  selected: !row.selected,
                };
              }
              return row;
            })
          : state.time,
      cost:
        type === 1
          ? state.cost.map(row => {
              if (row.id === selectedRow.id) {
                return {
                  ...row,
                  selected: !row.selected,
                };
              }
              return row;
            })
          : state.cost,
      travel:
        type === 2
          ? state.travel.map(row => {
              if (row.id === selectedRow.id) {
                return {
                  ...row,
                  selected: !row.selected,
                };
              }
              return row;
            })
          : state.travel,
    }));
  };

  handleSelectAllPriceGrids = type => {
    const {
      allTimePriceGridsSelected,
      allTravelPriceGridsSelected,
      allCostPriceGridsSelected,
      time,
      cost,
      travel,
    } = this.state;
    if (type === 0) {
      const isCurrentlySelected = !allTimePriceGridsSelected;
      this.setState({
        time: time.map(e => ({ ...e, selected: !!isCurrentlySelected })),
        allTimePriceGridsSelected: isCurrentlySelected,
      });
    }
    if (type === 1) {
      const isCurrentlySelected = !allCostPriceGridsSelected;
      this.setState({
        cost: cost.map(e => ({ ...e, selected: !!isCurrentlySelected })),
        allCostPriceGridsSelected: isCurrentlySelected,
      });
    }
    if (type === 2) {
      const isCurrentlySelected = !allTravelPriceGridsSelected;
      this.setState({
        travel: travel.map(e => ({ ...e, selected: !!isCurrentlySelected })),
        allTravelPriceGridsSelected: isCurrentlySelected,
      });
    }
  };

  renderResourceType = (type, rows, columns) => {
    if (!rows || !rows.length) return false;
    const {
      allTimePriceGridsSelected,
      allCostPriceGridsSelected,
      allTravelPriceGridsSelected,
    } = this.state;
    const allSelected =
      type === 0
        ? allTimePriceGridsSelected
        : type === 1
        ? allCostPriceGridsSelected
        : type === 2
        ? allTravelPriceGridsSelected
        : false;
    return (
      <tbody>
        <tr className="caption">
          <td>
            <Checkbox
              extraClass="margin5R"
              onClick={() => this.handleSelectAllPriceGrids(type)}
              value={allSelected}
            />
            <FormattedListValue list="resourcetype" id={type} />
          </td>
          <td colSpan={columns.length + 1} />
        </tr>
        {rows.map(row => (
          <tr key={row.id}>
            <td>
              <label>
                <Checkbox
                  extraClass="margin5R"
                  onClick={() => this.handlePriceGridSelect(row, type)}
                  value={row.selected}
                />
                {row.label}
              </label>
            </td>
            {columns.map(col => (
              <td key={`${row.id}-${col.id}`} className="price ">
                {row.cells && row.cells[col.id] && (
                  <PriceGridCell
                    value={row.cells[col.id]}
                    onChange={cell => this.updateCell(type, row.id, col.id, cell)}
                  />
                )}
              </td>
            ))}
            <td />
          </tr>
        ))}
      </tbody>
    );
  };

  isGlobalActionsDisabled = () => {
    const {
      time,
      cost,
      travel,
      allTimePriceGridsSelected,
      allCostPriceGridsSelected,
      allTravelPriceGridsSelected,
    } = this.state;
    if ((!time || !time.length) && (!cost || !cost.length) && (!travel || !time.travel))
      return true;
    if (
      time.some(x => x.selected) ||
      cost.some(x => x.selected) ||
      travel.some(x => x.selected) ||
      allTimePriceGridsSelected ||
      allCostPriceGridsSelected ||
      allTravelPriceGridsSelected
    )
      return false;

    return true;
  };

  getExampleAmount = () => {
    const { time, profiles } = this.state;
    if (!time || !time.length) return undefined;
    const first = time.find(x => x.selected);
    if (!first) return undefined;
    const defaultProfile = profiles.find(x => x.isDefault);
    if (!defaultProfile) return undefined;
    const { cells: { [defaultProfile.id]: { amount } = { amount: 0 } } = {} } = first;
    return amount;
  };

  updatePriceGridAmountBatch = amountUpdate => {
    const { gridId, intl } = this.props;
    const { time, cost, travel } = this.state;
    const resourceIds = [
      ...time.filter(x => x.selected).map(x => x.id),
      ...cost.filter(x => x.selected).map(x => x.id),
      ...travel.filter(x => x.selected).map(x => x.id),
    ];
    const model = {
      updateAllPriceGrids: amountUpdate.updateAllPriceGrids,
      priceGridId: gridId,
      priceProfileId: amountUpdate.selectedProfile,
      amountOrPercentage: amountUpdate.amountOrPercentage,
      method: amountUpdate.method,
      rounding: amountUpdate.rounding,
      roundingDecimals: amountUpdate.roundingDecimals,
      resourceIds,
    };

    return api.base.post(`${config.appUrls.api}/api/Price/AmountUpdate`, model).then(
      ({ data }) => {
        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;

        ToastrService.info(intl.formatMessage(messages.BackgroundJobQueued), undefined, onClick);
        this.setState({
          showUpdateAmountModal: false,
        });
        return true;
      },
      error => handleHttpError(intl)(error),
    );
  };

  MapPriceGridScreen(data, intl) {
    const {
      allTimePriceGridsSelected,
      allCostPriceGridsSelected,
      allTravelPriceGridsSelected,
    } = this.state;
    const { profiles, rows, ...grid } = data;
    /* Split rows into resource types */
    const resources = rows
      .filter(x => x.type !== 3 && x.type !== 4)
      .reduce(
        (result, row) => {
          const resourceType = resourceTypesProp[row.type];
          const codePrefix = row.code ? `${row.code} – ` : '';
          const selected =
            row.type === 0
              ? allTimePriceGridsSelected
              : row.type === 1
              ? allCostPriceGridsSelected
              : row.type === 2
              ? allTravelPriceGridsSelected
              : false;
          const label = `${codePrefix}${intl.formatMessage(
            listItemMsg('resourceCategory', row.id, row.name),
          )}`;

          return {
            ...result,
            [resourceType]: [
              ...result[resourceType],
              { ...row, label, selected: !!selected, code: row.code || '' },
            ],
          };
        },
        { time: [], cost: [], travel: [] },
      );

    resources.time.sort(dynamicSortMultiple('code', 'label'));
    resources.cost.sort(dynamicSortMultiple('code', 'label'));
    resources.travel.sort(dynamicSortMultiple('code', 'label'));

    this.setState(state => ({
      ...state,
      grid,
      showUpdateAmountModal: false,
      profiles: profiles.sort(dynamicSortMultiple('-isDefault', 'name')),
      ...resources,
    }));
    return data;
  }
  createExcelReport = () => {
    const { intl } = this.props;

    const urlParams = {
      culture: intl.locale,
      connectionId: SignalRService.getConnectionId(),
    };

    this.setState({ creatingReport: true });

    return api.base
      .get(`/api/Price/Report`, {
        params: urlParams,
      })
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
      })
      .catch(error => {
        handleHttpError(intl)(error);
      })
      .finally(() => {
        this.setState({ creatingReport: false });
      });
  };

  render() {
    const {
      grid,
      profiles,
      time,
      cost,
      travel,
      showUpdateAmountModal,
      showRecalcRegistrationPriceModal,
      creatingReport,
    } = this.state;
    const { intl, gridId } = this.props;

    return grid ? (
      <div className="panel">
        {showUpdateAmountModal && (
          <PriceGridAmountModal
            intl={intl}
            show
            profiles={profiles}
            onClose={() => this.setState({ showUpdateAmountModal: false })}
            onSave={amountUpdate => this.updatePriceGridAmountBatch(amountUpdate)}
            exampleAmount={this.getExampleAmount()}
          />
        )}
        {showRecalcRegistrationPriceModal && (
          <RecalcRegistrationPriceModal
            intl={intl}
            gridId={gridId}
            profiles={profiles}
            onClose={() => this.setState({ showRecalcRegistrationPriceModal: false })}
          />
        )}

        <div className="panel-heading">
          <h3 className="panel-title">
            <span>
              {intl.formatMessage(messages.PriceGrid)} - {grid.name}
            </span>
            <ZendeskHelp article="5434829034770" />
          </h3>
          <CanUpdate on="pricelist">
            <div className="panel-buttons">
              <Button
                btnClassName="btn btn-xs btn-default margin10R"
                onClick={() => this.createExcelReport()}
                disabled={creatingReport}
              >
                <span className="margin5R">
                  {creatingReport && <i className="fa fa-spinner fa-spin" />}
                  {!creatingReport && <FaFileExcel className="file-icon" />}
                </span>
                {intl.formatMessage(messages.ExportToExcel)}
              </Button>

              <Button
                btnClassName="btn btn-xs btn-default margin10R"
                onClick={() => this.setState({ showRecalcRegistrationPriceModal: true })}
              >
                <FaCalculator className="react-icon margin5R" />{' '}
                {intl.formatMessage(messages.RecalculateRegistrationPrices)}
              </Button>
              <Button
                btnClassName="btn btn-xs btn-default"
                onClick={() => this.setState({ showUpdateAmountModal: true })}
                disabled={this.isGlobalActionsDisabled()}
                title={
                  this.isGlobalActionsDisabled()
                    ? intl.formatMessage(messages.PriceGridGlobalActionsTooltip)
                    : ''
                }
              >
                <CurrencySymbol /> {intl.formatMessage(messages.UpdateAmount)}
              </Button>
            </div>
          </CanUpdate>
        </div>
        <div className="panel-body">
          <table className="table table-grid table-hover price-grid">
            <thead>
              <tr>
                <th />
                {profiles.map(this.mapProfileHeader)}
                <th className="profile" />
              </tr>
            </thead>
            {this.renderResourceType(0, time, profiles)}
            {this.renderResourceType(1, cost, profiles)}
            {this.renderResourceType(2, travel, profiles)}
          </table>
        </div>
      </div>
    ) : (
      <LoadingAnimation />
    );
  }
}

PriceGrid.propTypes = {
  gridId: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(PriceGrid);
